import { SelectMenuHandle, SelectMenuProps } from "./types";
import {
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { SelectOptions } from "../../../internal/components/SelectOptions";
import { useDebouncedCallback } from "../../../internal/hooks/useDebouncedCallback";
import { OptionsPopover } from "../../../internal/components/OptionsPopover/OptionsPopover";
import { OptionsDialog } from "../../../internal/components/OptionsDialog/OptionsDialog";
import { useProcessedOptions } from "../../../internal/hooks/useProcessedOptions";
import { buildSelectItems } from "../../../internal/functions/buildSelectItems";
import { useMenuInteraction } from "./internal/useMenuInteraction";
import { SelectItem } from "../../../internal/types/selectFieldInternalTypes";
import { useSelectOrchestration } from "../../../internal/hooks/useSelectOrchestration";
import { SearchField } from "../../../components";

/**
 * SelectMenu component for selecting a single option from a dropdown triggered by any element.
 *
 * Features:
 * - Attaches to any trigger element via a render prop
 * - Optional search input rendered inside the popover/dialog
 * - Lazy loading with pagination (page, offset, or group modes)
 * - Pinned options support
 * - Grouped options support
 * - Supports both popover and dialog display modes
 * - Adaptive display based on device (mobile uses dialog)
 * - Cache support for loadOptions results
 * - Full accessibility support with ARIA attributes
 * - Controlled selection state
 * - Configurable popover width
 *
 * @example
 * <SelectMenu
 *   trigger={(props) => <Button {...props}>Select a book</Button>}
 *   label="Books"
 *   value={selectedOption}
 *   onSelectedOptionChange={setSelectedOption}
 *   loadOptions={loadOptions}
 * />
 */
export const SelectMenu = forwardRef<SelectMenuHandle, SelectMenuProps>(
  (props, ref) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const {
      id: idProp,
      value: selectedOption,
      initialLoad: initialLoadProp,
      displayMenuAs: displayMenuAsProp,
      lazy,
      loadOptions: loadOptionsProp,
      label,
      searchPlaceholder,
      debounceMs = 200,
      onSelectedOptionChange,
      cache,
      pinned,
      virtualize,
      disableSearch,
      popoverWidth,
      trigger,
      onSearchChange,
      onMenuKeyDown,
      onImplicitClose,
      onExplicitClose,
    } = props;

    const groupToString =
      "groupToString" in props ? props.groupToString : undefined;
    const groupSorter = "groupSorter" in props ? props.groupSorter : undefined;

    const autoId = useId();
    const id = idProp ?? autoId;

    const {
      displayAs,
      initialLoad,
      initialLoadPerformed,
      setInitialLoadPerformed,
      options,
      loading,
      loadingMore,
      hasMore,
      loadOptions,
      loadMore,
    } = useSelectOrchestration({
      lazy,
      loadOptions: loadOptionsProp,
      cache,
      lazyOptions: "lazyOptions" in props ? props.lazyOptions : undefined,
      initialLoad: initialLoadProp,
      displayMenuAs: displayMenuAsProp,
      ref,
    });

    const [inputValue, setInputValue] = useState("");

    const searchValue = disableSearch ? "" : inputValue;

    const { pinnedSections, ungroupedItems, groupSections } =
      useProcessedOptions({
        options,
        pinned,
        searchValue,
        groupToString,
        groupSorter,
      });

    const { items, sectionsMeta } = useMemo(
      () =>
        buildSelectItems({
          pinnedSections,
          groupSections,
          ungroupedItems,
        }),
      [pinnedSections, groupSections, ungroupedItems],
    );

    const selectedItemIds = useMemo(
      () => new Set(selectedOption ? [selectedOption.id] : []),
      [selectedOption],
    );

    const handleItemActivate = useCallback(
      (item: SelectItem) => {
        if (
          item.type === "option" ||
          item.type === "pinned-option" ||
          item.type === "grouped-option"
        ) {
          onSelectedOptionChange(item.original);
        } else {
          onSelectedOptionChange(null);
        }
      },
      [onSelectedOptionChange],
    );

    const {
      isOpen,
      closeMenu,
      highlightedIndex,
      setHighlightedIndex,
      getTriggerProps,
      getMenuProps,
      getItemProps,
      listRef,
      menuContainerRef,
      handleKeyDown,
    } = useMenuInteraction({
      items: items,
      sectionsMeta,
      onItemActivate: handleItemActivate,
      selectedItemIds,
      displayAs,
      closeOnActivate: true,
      id,
      triggerRef,
      label,
      onImplicitClose,
      onExplicitClose,
    });

    const debouncedOptionLoader = useDebouncedCallback((value: string) => {
      loadOptions(value, { initial: true });
    }, debounceMs);

    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedOptionLoader(value);
        onSearchChange?.(value);
      },
      [debouncedOptionLoader, onSearchChange],
    );

    const handleInputKeyDown = useCallback(
      (e: KeyboardEvent) => {
        onMenuKeyDown?.(e);
        handleKeyDown(e);
      },
      [handleKeyDown, onMenuKeyDown],
    );

    const getMenuPropsWithKeyDown: typeof getMenuProps = useCallback(
      (opts) => {
        const menuProps = getMenuProps(opts);
        if (!onMenuKeyDown) return menuProps;
        return {
          ...menuProps,
          onKeyDown: (e: KeyboardEvent) => {
            onMenuKeyDown(e);
            menuProps.onKeyDown(e);
          },
        };
      },
      [getMenuProps, onMenuKeyDown],
    );

    useEffect(() => {
      const performInitialLoad = async () => {
        if (initialLoad === "immediate" && !initialLoadPerformed) {
          await loadOptions("", { initial: true });
          setInitialLoadPerformed(true);
        }
      };
      performInitialLoad();
    }, [
      loadOptions,
      initialLoadPerformed,
      initialLoad,
      setInitialLoadPerformed,
    ]);

    const prevIsOpenRef = useRef(false);
    useEffect(() => {
      const wasOpen = prevIsOpenRef.current;
      prevIsOpenRef.current = isOpen;

      if (isOpen && !wasOpen) {
        if (initialLoad === "open" && !initialLoadPerformed) {
          loadOptions("", { initial: true }).then(() => {
            setInitialLoadPerformed(true);
          });
        }
        const selectedIdx = items.findIndex(
          (item) => item.original.id === selectedOption?.id,
        );
        if (selectedIdx >= 0) {
          setHighlightedIndex(selectedIdx);
        }
      }

      if (!isOpen && wasOpen) {
        setInputValue("");
        loadOptions("", { initial: true });
      }
    }, [
      isOpen,
      initialLoad,
      initialLoadPerformed,
      loadOptions,
      setInitialLoadPerformed,
      items,
      selectedOption?.id,
      setHighlightedIndex,
    ]);

    useEffect(() => {
      if (!isOpen || displayAs !== "popover") return;

      const frameId = requestAnimationFrame(() => {
        if (!disableSearch) {
          searchRef.current?.focus();
        } else {
          menuContainerRef.current?.focus();
        }
      });

      return () => cancelAnimationFrame(frameId);
    }, [isOpen, displayAs, disableSearch, menuContainerRef]);

    const handleLoadMore = async () => {
      await loadMore(searchValue);
    };

    const handleClickOutside = useCallback(() => {
      closeMenu({ restoreFocus: false });
    }, [closeMenu]);

    const triggerProps = getTriggerProps();

    const menuPropsForAria = getMenuProps();
    const activeDescendant = menuPropsForAria["aria-activedescendant"];

    const handleSearchClear = useCallback(() => {
      setInputValue("");
      debouncedOptionLoader("");
      onSearchChange?.("");
    }, [debouncedOptionLoader, onSearchChange]);

    const searchInput = !disableSearch ? (
      <SearchField
        ref={searchRef}
        value={inputValue}
        onChange={
          handleInputChange as React.ChangeEventHandler<HTMLInputElement>
        }
        onKeyDown={
          handleInputKeyDown as React.KeyboardEventHandler<HTMLInputElement>
        }
        onClear={handleSearchClear}
        aria-activedescendant={activeDescendant}
        aria-controls={menuPropsForAria.id}
        placeholder={searchPlaceholder}
        type="search"
      />
    ) : null;

    if (displayAs === "dialog") {
      return (
        <>
          {trigger(triggerProps)}
          <OptionsDialog
            id={`${id}-dialog`}
            isOpen={isOpen}
            onClose={closeMenu}
            title={label}
            field={searchInput ?? undefined}
            initialFocusResolver={
              !disableSearch
                ? (elements) =>
                    elements.find((el) => el.tagName === "INPUT") || elements[0]
                : undefined
            }
          >
            <SelectOptions
              ref={listRef}
              isOpen
              items={items}
              sectionsMeta={sectionsMeta}
              getMenuProps={(opts) => {
                const menuProps = getMenuPropsWithKeyDown(opts);
                if (disableSearch) {
                  return menuProps;
                }
                return { ...menuProps, tabIndex: -1 };
              }}
              getItemProps={getItemProps}
              highlightedIndex={highlightedIndex}
              selectedOption={selectedOption}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              loading={loading}
              loadingMore={loadingMore}
              virtualize={virtualize}
            />
          </OptionsDialog>
        </>
      );
    }

    return (
      <>
        {trigger(triggerProps)}
        <OptionsPopover
          id={`${id}-popover`}
          referenceElement={triggerRef}
          open={isOpen && displayAs === "popover"}
          onClickOutside={handleClickOutside}
          width={popoverWidth ?? 320}
        >
          {searchInput}
          <SelectOptions
            ref={listRef}
            isOpen={isOpen}
            items={items}
            sectionsMeta={sectionsMeta}
            getMenuProps={(opts) => {
              const menuProps = getMenuPropsWithKeyDown(opts);
              if (disableSearch) {
                return menuProps;
              }
              return { ...menuProps, tabIndex: -1 };
            }}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedOption={selectedOption}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loading={loading}
            loadingMore={loadingMore}
            virtualize={virtualize}
          />
        </OptionsPopover>
      </>
    );
  },
);

SelectMenu.displayName = "SelectMenu";
